import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { WebPageSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const filteredVideos = useMemo(() => {
    if (activeCategory === 'all') return videos;
    return videos.filter((v) => v.category === activeCategory);
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];

  const categoryMap: Record<string, string> = {
    all: '全部', entertainment: '娱乐搞笑', music: '音乐舞蹈', food: '美食烹饪',
    travel: '旅行户外', tech: '科技数码', sports: '体育运动', lifestyle: '生活日常',
    knowledge: '知识科普', game: '游戏电竞',
  };

  const categoryLabel = categoryMap[activeCategory] || '全部';

  return (
    <>
      <ScrollToTop />
      <title>{`海角社区视频${categoryLabel} - 发现精彩社区视频内容`}</title>
      <meta name="description" content={`海角社区视频${categoryLabel}频道，汇聚海量优质${categoryLabel}内容。浏览最新最热的${categoryLabel}视频，发现精彩社区视频资源。`} />
      <meta name="keywords" content={`海角社区视频,${categoryLabel},海角社区视频${categoryLabel},社区视频平台`} />
      <link rel="canonical" href={`https://haijiao.shipin.com/category/${activeCategory}`} />
      <WebPageSchema
        title={`海角社区视频${categoryLabel} - 发现精彩社区视频内容`}
        description={`海角社区视频${categoryLabel}频道，汇聚海量优质${categoryLabel}内容。浏览最新最热的${categoryLabel}视频。`}
        url={`https://haijiao.shipin.com/category/${activeCategory}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://haijiao.shipin.com' },
          { name: '分类浏览', url: 'https://haijiao.shipin.com/category/all' },
          { name: categoryLabel, url: `https://haijiao.shipin.com/category/${activeCategory}` },
        ]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb />
        </div>

        {/* Category Header */}
        <section className="w-full bg-background-50 pb-6">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 rounded-2xl bg-accent-100 flex items-center justify-center">
                <i className={`${currentCategory.icon} text-accent-600 text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">{categoryLabel}</h1>
                <p className="text-sm text-foreground-500 mt-0.5">{currentCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full bg-background-50 sticky top-16 z-30 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50 shadow-sm'
                      : 'bg-transparent text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${activeCategory === cat.slug ? 'bg-background-50/20' : 'bg-accent-50'}`}>
                    <i className={`${cat.icon} ${activeCategory === cat.slug ? 'text-background-50' : 'text-accent-600'} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Video Grid */}
        <section className="w-full bg-background-100 py-8 md:py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-900">{filteredVideos.length}</strong> 个视频
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">排序：</span>
                <select className="text-sm bg-background-50 border border-background-200 rounded-lg px-3 py-1.5 text-foreground-700 focus:outline-none focus:border-primary-300">
                  <option>最新发布</option>
                  <option>最多播放</option>
                  <option>最多点赞</option>
                </select>
              </div>
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-video-line text-foreground-300 text-3xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无视频内容</p>
              </div>
            )}
          </div>
        </section>

        {/* Browse All Categories */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold text-foreground-900 mb-6 text-center">浏览更多分类</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {categories.filter(c => c.id !== 'all').map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-background-200 bg-background-50 hover:border-accent-200 hover:bg-accent-50/30'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    activeCategory === cat.slug ? 'bg-primary-100' : 'bg-accent-50'
                  }`}>
                    <i className={`${cat.icon} ${activeCategory === cat.slug ? 'text-primary-600' : 'text-accent-600'} text-lg`}></i>
                  </div>
                  <span className={`text-xs font-medium whitespace-nowrap ${
                    activeCategory === cat.slug ? 'text-primary-700' : 'text-foreground-700'
                  }`}>
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}